import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  RatingInput,
  P,
  Text,
  Notification,
  Collapse,
  Level,
  Button,
  ExpandableContent,
  Grid,
  Toaster,
  Rating,
  Toasts,
} from '@deseretbook/react-ui'
import { Link } from '../../core'
import { Analytics, Logger } from '../../../dbcom'
import { useUserReview } from './useUserReview'
import { formatDate } from './formatDate'

const logger = new Logger({ context: 'product/reviews/StarOnlyRating' })

/**
 * Handles review links and sending a Star Only Rating
 * @param {integer} productId - The id of the product
 * @param {string} productName - The name of the Product
 * @param {boolean} verifiedPurchaser - Has the user purchased the product?
 * @param {boolean} isMobile - Media query for mobile widths
 * @param {boolean} isNarrow - Media query for when content becomes narrow
 *
 * @returns {func} React Component
 */
export const StarOnlyRating = ({
  productId,
  productName,
  verifiedPurchaser,
  isMobile,
  isNarrow,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  // We show special text after a user submits a review for the first time.
  const [firstReview, setFirstReview] = useState(false)

  const currentUser = window.__USER__

  const [userReview, createOrUpdateReview] = useUserReview(
    currentUser && currentUser.id,
    productId,
  )

  // Creates the review page link. Rating is added if user has selected a star rating.
  const reviewPageLink = `/products/${productId}/reviews/new${
    userReview ? `?rating=${userReview.rating}` : ''
  }`

  // Returns true if the user has submitted a review with a title and/or a review
  const userHasFullReview =
    userReview && (userReview.title || userReview.review)

  // Decides which title to use based off of the users review, and if they have purchased the product.
  let title

  if (userHasFullReview) {
    title = 'My Review'
  } else if (userReview) {
    title = 'Thanks for the Rating!'
  } else if (verifiedPurchaser) {
    title = 'How are you enjoying it?'
  } else {
    title = 'Ever use this product before?'
  }

  /**
   * Submits a rating only review.
   * @param {obj} target - the event target
   */
  const handleSubmit = async target => {
    const { value } = target
    // String for Update/Create verb usage in logging
    const reviewAction = userReview ? 'Update' : 'Create'

    Analytics.track(
      {
        category: 'Product Rating',
        action: `${reviewAction} rating only review`,
        label: `Set rating only to ${value} stars`,
      },
      { isInteractive: true },
    )

    setIsSubmitting(true)

    const userRating = {
      rating: value,
      userId: currentUser && currentUser.id,
      name: (currentUser && currentUser.name) || null,
    }

    try {
      const reviewResponse = await createOrUpdateReview(userRating)

      if (reviewResponse) {
        Toasts.success('Your rating has been updated')
      } else {
        setFirstReview(true)
      }
    } catch (error) {
      logger.error(`Unable to ${reviewAction} star only review: ${error}`)
      Toasts.warning(
        `Unable to ${reviewAction} the review.`,
        'Please reload the page.',
      )
    }
    setIsSubmitting(false)
  }

  // Grid settings for Full Reviews
  const baseFullReviewGridSettings = {
    areas: ['title link', 'rating rating', 'text text'],
    columns: Grid.repeat(2, Grid.fr(1)),
    rows: [2, 4, Grid.fr(1)],
    justifyItems: 'center',
  }
  const largeFullReviewGridSettings = {
    areas: ['title rating link', 'text text text'],
    columns: [
      Grid.minMax(Grid.minContent, Grid.maxContent),
      Grid.fr(2),
      Grid.minMax(Grid.minContent, Grid.maxContent),
    ],
    columnGap: 1,
    rows: [4, Grid.fr(1)],
    justifyItems: 'left',
  }

  // Grid settings for No or Star only reviews
  const basePartialReviewGridSettings = {
    areas: ['title', 'rating', 'text', 'link'],
    columns: Grid.fr(1),
    rows: [2, 4, Grid.fr(1), Grid.minMax(Grid.minContent, Grid.maxContent)],
    justifyItems: 'center',
  }
  const largePartialReviewGridSettings = {
    areas: ['title rating link', 'text text text'],
    columns: [
      Grid.minMax(Grid.minContent, Grid.maxContent),
      Grid.fr(1),
      Grid.minMax(Grid.minContent, Grid.maxContent),
    ],
    // The thanks for the rating title is too long for a 1rem column gap. We change it to 0.25 to fit
    columnGap: title.length >= 22 ? 0.25 : 1,
    rows: [3, Grid.fr(1)],
    justifyItems: 'left',
  }

  return (
    currentUser &&
    currentUser.loggedIn && (
      <Box marginVertical={2} isFullwidth>
        <Toaster />
        <Notification isCloseable={false}>
          <Box isFullwidth padding={1}>
            {
              // User has submitted a Star Rating, but not a full review.
              // User has submitted a full review. (it has title, review, and rating)
            }
            {userHasFullReview && (
              <Grid
                {...baseFullReviewGridSettings}
                alignItems="center"
                breakpoints={{
                  640: { ...largeFullReviewGridSettings },
                  768: { ...baseFullReviewGridSettings },
                  995: { ...largeFullReviewGridSettings },
                }}
              >
                <Grid.Item area="title" justify="left">
                  <Text as="h3" size="large">
                    {title}
                  </Text>
                </Grid.Item>
                <Grid.Item area="rating">
                  <Rating size="medium" average={userReview.rating} />
                </Grid.Item>
                <Grid.Item area="link" justify="right">
                  <Button
                    aria-label="Update my review"
                    href={reviewPageLink}
                    as={Link}
                  >
                    Update
                  </Button>
                </Grid.Item>
                <Grid.Item area="text">
                  <Level>
                    <Level.Left>
                      <Level.Item>
                        <Text isBold>{userReview.title}</Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text color="secondary" shade="light">
                          {formatDate(userReview.createdAt)}
                        </Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                  <ExpandableContent collapsedHeight={6}>
                    <P>{userReview.review}</P>
                  </ExpandableContent>
                </Grid.Item>
              </Grid>
            )}
            {
              // User has not written a review and has purchased the product.
              // User has not written a review and has not purchased the product.
            }
            {!userHasFullReview && (
              <Grid
                {...basePartialReviewGridSettings}
                alignItems="center"
                justifyItems="left"
                breakpoints={{
                  640: { ...largePartialReviewGridSettings },
                  768: { ...basePartialReviewGridSettings },
                  995: { ...largePartialReviewGridSettings },
                }}
              >
                <Grid.Item area="title">
                  <Text as="h3" alignment="center" size="large">
                    {title}
                  </Text>
                </Grid.Item>
                <Grid.Item area="rating">
                  <Box alignItems="center">
                    <RatingInput
                      name="rating-only"
                      aria-label="How would you rate this product on a scale of 1 to 5?"
                      onChange={({ target }) => handleSubmit(target)}
                      selectedValue={userReview ? userReview.rating : 0}
                      disabled={isSubmitting}
                      required
                      removeHint
                      size={isMobile ? 'large' : 'medium'}
                    />
                  </Box>
                </Grid.Item>
                <Grid.Item
                  area="link"
                  justify={isMobile || isNarrow ? 'center' : 'right'}
                >
                  <Collapse isCollapsed={!userReview}>
                    <Button color="primary" href={reviewPageLink} as={Link}>
                      Finish Review
                    </Button>
                  </Collapse>
                </Grid.Item>
                <Grid.Item area="text">
                  {firstReview || userReview ? (
                    <Text>
                      Have more to say? Other customers would love to hear your
                      opinion.
                    </Text>
                  ) : (
                    <Text>
                      {productName &&
                        verifiedPurchaser &&
                        `Thank you for purchasing ${productName}. `}
                      Share your experience with this product to help others
                      make a decision.
                    </Text>
                  )}
                </Grid.Item>
              </Grid>
            )}
          </Box>
        </Notification>
      </Box>
    )
  )
}

StarOnlyRating.propTypes = {
  productId: PropTypes.number.isRequired,
  productName: PropTypes.string,
  verifiedPurchaser: PropTypes.bool,
  isMobile: PropTypes.bool,
  isNarrow: PropTypes.bool,
}
